// NPM
import Axios, { AxiosResponse } from "axios";
import moment from "moment";

// Interfaces
import IAppointmentRecord from "../interfaces/IAppointmentRecord";
import IAppointmentType from "../interfaces/IAppointmentType";
import IAppointmentTypeProviderDto from "../interfaces/IAppointmentTypeProviderDto";
import IAvailableSlot from "../interfaces/IAvailableSlot";
import IDoctor from "../interfaces/IDoctor";
import IPatientDetails from "../interfaces/IPatientDetails";
import ITelehealthDay from "../interfaces/ITelehealthDay";

// Config
import { LOCAL_HOST, PRACTIQ_API_ENDPOINT, PRACTIQ_API_KEY } from "../utility/config";

// Classes
import DateTime from "./DateTime";
import Doctor from "./Doctor";

export default class AppointmentRecord {
	private static readonly apiHost = process.env.NODE_ENV === "production" ? PRACTIQ_API_ENDPOINT : LOCAL_HOST;


	public static async findFirstAvailable(): Promise<string> {
		const now = moment();

		let firstAvailableDate: string | null = null;

		for (let i = 0; i < DateTime.FORTNIGHT; i++) {
			const result = await AppointmentRecord.getAppointmentsBetween(
				now.format(DateTime.DEFAULT_DATE_FORMAT),
				now.format(DateTime.DEFAULT_DATE_FORMAT)
			);

			const filteredRecords = AppointmentRecord.filterRecords(
				result.data
			);

			if (filteredRecords.length > 0) {
				firstAvailableDate = now.format(DateTime.DEFAULT_DATE_FORMAT);
				break;
			}

			now.add(1, "day");
		}

		return new Promise((resolve, reject) => {
			if (firstAvailableDate) {
				resolve(firstAvailableDate);
			} else {
				reject("No available appointment for the next 14 days.");
			}
		});
	}

	public static async getAppointmentsBetween(
		fromDate: string,
		toDate: string
	): Promise<AxiosResponse<IAppointmentRecord[]>> {
		const params: URLSearchParams = new URLSearchParams();
		params.append("dateFrom", fromDate);
		params.append("dateTo", toDate);

		return await Axios.request<IAppointmentRecord[]>({
			url: this.apiHost + "/appointment/available-appointments",
			method: "GET",
			params: params,
			headers:{
				"X-Api-Key": PRACTIQ_API_KEY
			}
		});
	}

	public static filterOutEmptyRecords(
		records: IAppointmentRecord[]
	): IAppointmentRecord[] {
		const allRecords: IAppointmentRecord[] = records.filter(
			(rec: IAppointmentRecord) => rec.availableSlots.length > 0
		);

		return allRecords;
	}

	public static filterPastAppointmentSlots(
		records: IAppointmentRecord[]
	): IAppointmentRecord[] {
		const newRecords: IAppointmentRecord[] = [];

		for (let iR = 0; iR < records.length; iR++) {
			const currentRecord = records[iR];
			const slots = currentRecord.availableSlots.filter(
				(item: IAvailableSlot) => {
					const apptTime = moment(item.startDateTime);
					const now = moment();

					return apptTime.isAfter(now);
				}
			);
			if (slots.length > 0) {
				newRecords.push({ ...currentRecord, availableSlots: slots });
			}
		}

		return newRecords;
	}

	public static filterRecords(records: IAppointmentRecord[]) {
		const filteredRecords: IAppointmentRecord[] =
			AppointmentRecord.filterOutEmptyRecords(records);

		const currentRecords: IAppointmentRecord[] =
			AppointmentRecord.filterPastAppointmentSlots(filteredRecords);
			
		return currentRecords;
	}

	public static filterByAppointmentType(
		appointmentRecord: IAppointmentRecord,
		providerIdList: IAppointmentTypeProviderDto[],
		appointmentType: IAppointmentType,
		doctors: IDoctor[],
		patientDetails: IPatientDetails
	): boolean {
		// console.log(providerIdList);
		// console.log(appointmentType.appointmentTypeId);
		// return true;

		const currentDr = Doctor.getDoctorByBPId(
			appointmentRecord.userId.toString(),
			doctors
		);

		// Utility.debug(currentDr);

		if (!currentDr) return false;

		const telehealthSchedule = currentDr.TelehealthDays as ITelehealthDay[];

		return (
			providerIdList.filter((providerId: IAppointmentTypeProviderDto) => {
				const provided =
					parseInt(providerId.bpId) === appointmentRecord.userId;

				let deliveryMethodProvided = false;

				if (
					Doctor.canProvideTelehealth(
						appointmentType.deliveryMethod,
						telehealthSchedule,
						moment(appointmentRecord.dateAt).isoWeekday()
					)
				)
					deliveryMethodProvided = true;

				if (
					Doctor.isAvailableForOnsiteAppt(
						appointmentType.deliveryMethod,
						telehealthSchedule,
						moment(appointmentRecord.dateAt).isoWeekday()
					)
				)
					deliveryMethodProvided = true;

				if (appointmentType.deliveryMethod === 0)
					deliveryMethodProvided = true;

				let allowNewPatient =
					Doctor.acceptsNewPatient(
						providerIdList,
						parseInt(currentDr.ClinixId as string)
					) || !patientDetails.isNewPatient;

				return provided && deliveryMethodProvided && allowNewPatient;
			}).length !== 0
		);
	}
}
